import React, { useState } from 'react';
import * as gamesApi from '../../api/games';
import './gameDetails.css';

/**
 * Grow a mini-league out of this game.
 *
 * Two outcomes, and which one you get depends on whether you run the game. The
 * manager converts the game itself: it keeps its players, its code and its week,
 * and its points carry straight onto the new league's table. Anyone else starts
 * their own empty league and this game is left exactly where it is.
 *
 * Both are offered, because "create a mini-league" is a reasonable thing to want
 * from here either way - but they are worded apart, because being told you had
 * created a league and then finding your game still standalone is the same kind
 * of surprise this whole change exists to remove.
 */
function CreateLeagueFromGamePanel({ game, isManager, onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  // A game that already belongs to a league has nothing to offer here.
  if (game?.miniLeagueTournament) {
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      setCreated(await gamesApi.createLeagueFromGame(game.id, name));
    } catch (err) {
      setError(err.body?.error || 'Could not create the mini-league');
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <div className="gd-panel">
        <div className="gd-panel-main">
          <p className="gd-panel-title">{created.leagueName} created</p>
          <p className="gd-panel-body">
            {created.adoptedGame
              ? 'This game now runs under it, and everyone in it is a member. Its points are already on the table.'
              : 'Your new league is empty and this game is unchanged - share the code to get people in.'}
          </p>
          <p className="gd-panel-code">{created.leagueCode}</p>
        </div>
        <button type="button" className="gd-panel-btn is-add" onClick={() => onCreated && onCreated()}>
          Done
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="gd-panel">
        <div className="gd-panel-main">
          <p className="gd-panel-title">Make this a mini-league</p>
          <p className="gd-panel-body">
            {isManager
              ? 'Turn this game into a mini-league. It keeps its players and its points, and carries on running inside the league.'
              : 'Start your own mini-league. This game stays exactly as it is - you get a new league to invite people to.'}
          </p>
        </div>
        <button type="button" className="gd-panel-btn is-add" onClick={() => setOpen(true)}>
          {isManager ? 'Create mini-league' : 'Start my own league'}
        </button>
      </div>
    );
  }

  return (
    <div className="gd-panel">
      <form className="gd-panel-main" onSubmit={submit}>
        <p className="gd-panel-title">{isManager ? 'Make this a mini-league' : 'Start your own mini-league'}</p>
        <p className="gd-panel-body">
          {isManager
            ? `${game.name} and everyone in it move into the new league.`
            : `${game.name} is not affected - this creates a separate league that you run.`}
        </p>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="League name"
          maxLength={100}
          autoFocus
          required
          className="gd-panel-input"
        />
        {error && <p className="gd-panel-error">{error}</p>}
        <div className="gd-panel-actions">
          <button type="submit" className="gd-panel-btn is-add" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create league'}
          </button>
          <button
            type="button"
            className="gd-panel-btn is-cancel"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}


export default CreateLeagueFromGamePanel;
